/**
 * Shape recognition for freehand strokes.
 *
 * Fits a hand-drawn stroke to a regular primitive: line, circle, ellipse,
 * triangle, rectangle or quadrilateral. Works on SCREEN points so every
 * tolerance is in pixels or relative to the stroke size, independent of the
 * zoom level. Pure geometry, no ODA dependency.
 */

export interface Pt {
  x: number;
  y: number;
}

/** "polygon": 5 sides or more, kept as drawn (corners straightened) */
export type PolygonType =
  | "triangle"
  | "rectangle"
  | "quadrilateral"
  | "polygon";

export type RecognizedShape =
  | { kind: "line"; start: Pt; end: Pt }
  | { kind: "circle"; center: Pt; radius: number }
  | {
      kind: "ellipse";
      center: Pt;
      majorRadius: number;
      minorRadius: number;
      /** Angle of the major axis, in radians */
      rotation: number;
    }
  | { kind: "polygon"; type: PolygonType; vertices: Pt[] };

/** Strokes smaller than this (in pixels) are never recognized */
const MIN_STROKE_SIZE = 20;

/**
 * Points closer than this (in pixels) to the previous kept point are dropped:
 * hand tremor (pen landing, slowing down, holding still) makes tiny
 * back-and-forth moves that inflate the path length and bend the contour.
 */
const MIN_SAMPLE_SPACING = 4;

/**
 * Where the pen lands and where it stops before lifting, the hand trembles in
 * place: points within this radius (in pixels) of the first point, at the
 * start, and of the last point, at the end, are trimmed.
 */
const TREMOR_RADIUS = 15;

/** Line: max distance to the chord, relative to the chord length */
const LINE_MAX_DEVIATION = 0.08;
/**
 * Line: how far (relative to the chord length) the stroke may run past its
 * end points along the chord. Rules out going back and forth over a segment.
 */
const LINE_MAX_OVERRUN = 0.1;

/** Closed shapes: max gap between start and end, relative to the contour length */
const CLOSE_MAX_GAP = 0.2;
/** Overshoot search starts once this fraction of the stroke has been drawn */
const CLOSE_SEARCH_FROM = 0.6;

/** Closed contours are resampled to this many evenly spaced points */
const RESAMPLE_COUNT = 96;

/** Ellipse/circle: max mean relative radial error */
const ELLIPSE_MAX_ERROR = 0.12;
/** Minor/major ratio above which an ellipse becomes a circle */
const CIRCLE_MIN_RATIO = 0.75;
/** Minor/major ratio below which the stroke is too flat to be an ellipse */
const ELLIPSE_MIN_RATIO = 0.15;

/** Polygon: RDP tolerance, relative to the contour perimeter */
const POLYGON_SIMPLIFY = 0.05;
/** Polygon: a vertex turning less than this is merged into its edge */
const POLYGON_MIN_TURN = (25 * Math.PI) / 180;
/**
 * Polygon: edges shorter than this (relative to the perimeter) are the bevel
 * of a rounded corner, and their two vertices are merged into one
 */
const POLYGON_SHORT_EDGE = 0.08;
/** Polygon: max mean distance to the edges, relative to the equivalent radius */
const POLYGON_MAX_ERROR = 0.1;
/**
 * A polygon is chosen over an ellipse unless its fit error is more than this
 * many times the ellipse's
 */
const POLYGON_PREFERENCE = 1.5;
/**
 * Polygons of 5+ sides: finer RDP tolerance (relative to the perimeter), as
 * their corners stand out less from the contour
 */
const MANY_SIDED_SIMPLIFY = 0.035;
/** Polygons with more sides than this are left to the ellipse */
const POLYGON_MAX_VERTICES = 8;
/** Samples on each side of a vertex counted as part of its corner */
const CORNER_WINDOW = 3;
/**
 * Polygons of 5+ sides: the stretches between corners may turn at most this
 * fraction of what they would turn on an evenly curved contour (a circle
 * gives about 1, a hand-drawn polygon about 0.4 to 0.55)
 */
const MAX_TURNING_BETWEEN_CORNERS = 0.6;
/** Rectangle: tolerance on each corner angle */
const RIGHT_ANGLE_TOLERANCE = (20 * Math.PI) / 180;

/** Orientations within this angle of horizontal/vertical are snapped to it */
const AXIS_SNAP = (8 * Math.PI) / 180;

/**
 * Total turning is measured on the contour resampled to this many points:
 * coarse enough that hand tremor between two samples is negligible.
 */
const TURNING_SAMPLE_COUNT = 32;
/**
 * A closed contour may turn in total at most this many full turns (summing
 * the absolute value of every bend). A simple convex shape turns exactly
 * once; dents, zigzags and loops add turning both ways.
 */
const MAX_TOTAL_TURNS = 1.15;

/** Line: total turning is measured on the stroke resampled to this many points */
const LINE_TURNING_SAMPLE_COUNT = 16;
/** Line: max total turning (sum of absolute bends) along the stroke */
const LINE_MAX_TURNING = Math.PI / 2;

interface Candidate {
  shape: RecognizedShape;
  /** Normalized fit error (lower is better) */
  error: number;
  /** Polygon whose corners were checked to be sharp (see hasSharpCorners) */
  sharpCorners?: boolean;
}

/**
 * Recognize the shape drawn by a freehand stroke.
 * @param points Stroke points in screen coordinates, in drawing order
 * @returns The regularized shape, or null if the stroke matches nothing
 */
export function recognizeShape(rawPoints: Pt[]): RecognizedShape | null {
  const points = trimTremor(removeJitter(rawPoints));
  if (points.length < 3) return null;

  const length = pathLength(points, false);
  if (length < MIN_STROKE_SIZE) return null;

  const line = recognizeLine(points);
  if (line) return line;

  const contour = closedContour(points, length);
  if (!contour) return null;

  const samples = resample(contour, RESAMPLE_COUNT);
  const bounds = boundsOf(samples);
  if (
    Math.max(bounds.maxX - bounds.minX, bounds.maxY - bounds.minY) <
    MIN_STROKE_SIZE
  ) {
    return null;
  }

  if (!turnsOnce(contour)) return null;

  // The polygon is favored: a rectangle with bowed edges often fits an
  // ellipse about as well as it fits a rectangle. It only loses when the
  // ellipse fits clearly better (an elongated ellipse can pass for a
  // quadrilateral, but a much worse one). A polygon whose sharp corners were
  // checked always wins: a heptagon fits a circle well, but a circle never
  // has sharp corners.
  const perimeter = pathLength(samples, true);
  const polygon = fitPolygon(samples, perimeter);
  const ellipse = fitEllipse(samples);
  if (
    polygon &&
    (polygon.sharpCorners ||
      !ellipse ||
      polygon.error <= ellipse.error * POLYGON_PREFERENCE)
  ) {
    return polygon.shape;
  }
  return ellipse?.shape ?? null;
}

/**
 * The shapes we recognize are convex: following the contour, it turns by one
 * full turn, always the same way. A jagged outline (moisture stain, spalling),
 * a figure eight or a scribble turns much more in total, even when its mean
 * distance to an ellipse or a polygon happens to be small.
 */
function turnsOnce(contour: Pt[]): boolean {
  const samples = smoothSamples(resample(contour, TURNING_SAMPLE_COUNT, true));
  return totalTurning(samples, true) <= MAX_TOTAL_TURNS * 2 * Math.PI;
}

/**
 * A line barely bends: a slightly wavy stroke (a crack, a contour edge)
 * stays close to its chord but keeps turning one way and the other.
 */
function staysStraight(points: Pt[]): boolean {
  const samples = smoothSamples(
    resample(points, LINE_TURNING_SAMPLE_COUNT, false),
    false,
  );
  return totalTurning(samples, false) <= LINE_MAX_TURNING;
}

/**
 * Average each sample with its two neighbors (an open stroke keeps its end
 * points). Hand tremor makes every sample bend a few degrees each way, which
 * adds up along the contour; averaging cancels most of it without changing a
 * convex shape into a concave one or the reverse.
 */
function smoothSamples(points: Pt[], closed = true): Pt[] {
  const n = points.length;
  return points.map((p, i) => {
    if (!closed && (i === 0 || i === n - 1)) return p;
    const prev = points[(i + n - 1) % n];
    const next = points[(i + 1) % n];
    return { x: (prev.x + p.x + next.x) / 3, y: (prev.y + p.y + next.y) / 3 };
  });
}

/**
 * Sum of the absolute bend angles along a contour, in radians. A closed
 * contour also bends where it wraps around, at its first point.
 */
function totalTurning(points: Pt[], closed = true): number {
  let total = 0;
  const n = points.length;
  for (let i = closed ? 0 : 1; i < (closed ? n : n - 1); i++) {
    total += bendAt(points, i);
  }
  return total;
}

/** Absolute bend angle of a closed contour at point i, in radians */
function bendAt(points: Pt[], i: number): number {
  const n = points.length;
  const prev = points[(i + n - 1) % n];
  const curr = points[i];
  const next = points[(i + 1) % n];
  const a1 = Math.atan2(curr.y - prev.y, curr.x - prev.x);
  const a2 = Math.atan2(next.y - curr.y, next.x - curr.x);
  let turn = a2 - a1;
  while (turn > Math.PI) turn -= 2 * Math.PI;
  while (turn < -Math.PI) turn += 2 * Math.PI;
  return Math.abs(turn);
}

/**
 * Drop the tremor where the pen landed and where it stopped: the stroke
 * starts at the last point within TREMOR_RADIUS of the first one, and ends at
 * the first point within TREMOR_RADIUS of the last one.
 */
function trimTremor(points: Pt[]): Pt[] {
  if (points.length < 3) return points;
  const first = points[0];
  const last = points[points.length - 1];

  let start = 0;
  while (
    start < points.length - 1 &&
    dist(points[start + 1], first) <= TREMOR_RADIUS
  ) {
    start++;
  }
  let end = points.length - 1;
  while (end > start && dist(points[end - 1], last) <= TREMOR_RADIUS) {
    end--;
  }

  // Keep the anchors themselves so the stroke still reaches both ends
  const trimmed = [first, ...points.slice(start + 1, end), last];
  return trimmed.length >= 3 ? trimmed : points;
}

/** Keep only points at least MIN_SAMPLE_SPACING away from the previous kept one */
function removeJitter(points: Pt[]): Pt[] {
  if (points.length === 0) return points;
  const result = [points[0]];
  for (const p of points) {
    if (dist(p, result[result.length - 1]) >= MIN_SAMPLE_SPACING) {
      result.push(p);
    }
  }
  return result;
}

// ============== Line ==============

/**
 * A line is a stroke that stays close to its chord and never runs past its
 * end points. The path length is deliberately NOT used: tremor where the pen
 * lands or stops adds length without making the stroke any less straight.
 */
function recognizeLine(points: Pt[]): RecognizedShape | null {
  const start = points[0];
  const end = points[points.length - 1];
  const chord = dist(start, end);

  if (chord < MIN_STROKE_SIZE) return null;

  const ux = (end.x - start.x) / chord;
  const uy = (end.y - start.y) / chord;
  for (const p of points) {
    if (distToLine(p, start, end) > LINE_MAX_DEVIATION * chord) {
      return null;
    }
    const along = (p.x - start.x) * ux + (p.y - start.y) * uy;
    if (
      along < -LINE_MAX_OVERRUN * chord ||
      along > (1 + LINE_MAX_OVERRUN) * chord
    ) {
      return null;
    }
  }

  if (!staysStraight(points)) return null;

  // Snap near-horizontal/vertical lines, rotating around the midpoint
  const angle = Math.atan2(end.y - start.y, end.x - start.x);
  const snapped = snapAngle(angle, Math.PI / 2);
  if (snapped === angle) {
    return { kind: "line", start, end };
  }

  const mid = { x: (start.x + end.x) / 2, y: (start.y + end.y) / 2 };
  const half = {
    x: (Math.cos(snapped) * chord) / 2,
    y: (Math.sin(snapped) * chord) / 2,
  };
  return {
    kind: "line",
    start: { x: mid.x - half.x, y: mid.y - half.y },
    end: { x: mid.x + half.x, y: mid.y + half.y },
  };
}

// ============== Closed contour ==============

/**
 * Extract the first lap of a closed stroke, or null if the stroke is open.
 * Users often overshoot the start point: past CLOSE_SEARCH_FROM of the stroke,
 * the point that comes closest to the start marks the end of the lap and
 * anything after it is dropped.
 */
function closedContour(points: Pt[], length: number): Pt[] | null {
  const start = points[0];
  let travelled = 0;
  let bestIndex = -1;
  let bestDist = Infinity;
  let bestTravelled = 0;

  for (let i = 1; i < points.length; i++) {
    travelled += dist(points[i - 1], points[i]);
    if (travelled < CLOSE_SEARCH_FROM * length) continue;

    const d = dist(points[i], start);
    if (d < bestDist) {
      bestDist = d;
      bestIndex = i;
      bestTravelled = travelled;
    }
  }

  if (bestIndex < 2 || bestDist > CLOSE_MAX_GAP * bestTravelled) {
    return null;
  }

  return points.slice(0, bestIndex + 1);
}

// ============== Ellipse / circle ==============

function fitEllipse(samples: Pt[]): Candidate | null {
  const c = centroid(samples);

  // Principal axes from the covariance matrix
  let sxx = 0;
  let syy = 0;
  let sxy = 0;
  for (const p of samples) {
    const dx = p.x - c.x;
    const dy = p.y - c.y;
    sxx += dx * dx;
    syy += dy * dy;
    sxy += dx * dy;
  }
  let theta = 0.5 * Math.atan2(2 * sxy, sxx - syy);

  // Radii from the RMS spread along each axis (for points spread around an
  // ellipse, mean(u²) = a²/2). Unlike the extents, one wobble or spike of the
  // hand barely moves them. The contour is resampled evenly along its length,
  // so the centroid is a sound center.
  const center = c;
  const cosT = Math.cos(theta);
  const sinT = Math.sin(theta);
  let su = 0;
  let sv = 0;
  for (const p of samples) {
    const dx = p.x - c.x;
    const dy = p.y - c.y;
    const pu = dx * cosT + dy * sinT;
    const pv = -dx * sinT + dy * cosT;
    su += pu * pu;
    sv += pv * pv;
  }
  let a = Math.sqrt((2 * su) / samples.length);
  let b = Math.sqrt((2 * sv) / samples.length);

  // The RMS estimate is biased for elongated ellipses (points are spaced
  // evenly along the curve, not in angle): refine by least squares, giving
  // each point its parametric angle on the current estimate
  for (let iteration = 0; iteration < 3 && a > 0 && b > 0; iteration++) {
    let numA = 0;
    let denA = 0;
    let numB = 0;
    let denB = 0;
    for (const p of samples) {
      const dx = p.x - c.x;
      const dy = p.y - c.y;
      const pu = dx * cosT + dy * sinT;
      const pv = -dx * sinT + dy * cosT;
      const phi = Math.atan2(pv / b, pu / a);
      const cosPhi = Math.cos(phi);
      const sinPhi = Math.sin(phi);
      numA += pu * cosPhi;
      denA += cosPhi * cosPhi;
      numB += pv * sinPhi;
      denB += sinPhi * sinPhi;
    }
    a = numA / denA;
    b = numB / denB;
  }

  if (a < b) {
    [a, b] = [b, a];
    theta += Math.PI / 2;
  }
  if (a <= 0 || b / a < ELLIPSE_MIN_RATIO) return null;

  if (b / a >= CIRCLE_MIN_RATIO) {
    let radius = 0;
    for (const p of samples) {
      radius += dist(p, center);
    }
    radius /= samples.length;
    let error = 0;
    for (const p of samples) {
      error += Math.abs(dist(p, center) / radius - 1);
    }
    error /= samples.length;
    if (error > ELLIPSE_MAX_ERROR) return null;
    return { shape: { kind: "circle", center, radius }, error };
  }

  const cos = Math.cos(theta);
  const sin = Math.sin(theta);
  let error = 0;
  for (const p of samples) {
    const dx = p.x - center.x;
    const dy = p.y - center.y;
    const x = (dx * cos + dy * sin) / a;
    const y = (-dx * sin + dy * cos) / b;
    error += Math.abs(Math.hypot(x, y) - 1);
  }
  error /= samples.length;
  if (error > ELLIPSE_MAX_ERROR) return null;

  return {
    shape: {
      kind: "ellipse",
      center,
      majorRadius: a,
      minorRadius: b,
      rotation: snapAngle(normalizeAngle(theta, Math.PI), Math.PI / 2),
    },
    error,
  };
}

// ============== Polygon ==============

function fitPolygon(samples: Pt[], perimeter: number): Candidate | null {
  return fitFewSided(samples, perimeter) ?? fitManySided(samples, perimeter);
}

/** Corners of a closed contour, simplified with the given RDP tolerance */
function polygonVertices(samples: Pt[], perimeter: number, simplify: number) {
  let vertices = simplifyClosed(samples, simplify * perimeter);
  vertices = removeFlatVertices(vertices);
  vertices = mergeRoundedCorners(vertices, POLYGON_SHORT_EDGE * perimeter);
  return removeFlatVertices(vertices);
}

/**
 * Mean distance to the polygon, relative to the radius of a circle of the
 * same perimeter, so it compares with the ellipse's relative radial error
 */
function polygonError(samples: Pt[], vertices: Pt[], perimeter: number) {
  const scale = perimeter / (2 * Math.PI);
  let error = 0;
  for (const p of samples) {
    error += distToPolygon(p, vertices);
  }
  return error / (samples.length * scale);
}

/**
 * Polygon of 5 to POLYGON_MAX_VERTICES sides. Its corners are shallower, so
 * they need a finer simplification, which also cuts a circle into a polygon
 * of about as many sides: only sharp corners tell the two apart.
 */
function fitManySided(samples: Pt[], perimeter: number): Candidate | null {
  const vertices = polygonVertices(samples, perimeter, MANY_SIDED_SIMPLIFY);
  if (vertices.length < 5 || vertices.length > POLYGON_MAX_VERTICES) {
    return null;
  }

  const error = polygonError(samples, vertices, perimeter);
  if (error > POLYGON_MAX_ERROR || !hasSharpCorners(samples, vertices)) {
    return null;
  }
  return {
    shape: { kind: "polygon", type: "polygon", vertices },
    error,
    sharpCorners: true,
  };
}

/**
 * Whether the contour turns at the vertices rather than all along. On a
 * polygon nearly all the turning happens at the corners; on a circle or an
 * ellipse it is spread evenly, so the stretches between vertices turn about
 * as much as the stretches around them.
 */
function hasSharpCorners(samples: Pt[], vertices: Pt[]): boolean {
  const smoothed = smoothSamples(samples);
  const n = smoothed.length;
  const bends = smoothed.map((_, i) => bendAt(smoothed, i));
  const total = bends.reduce((sum, bend) => sum + bend, 0);

  // Samples around each vertex, counted as part of its corner
  const corners = new Set<number>();
  for (const vertex of vertices) {
    const nearest = nearestIndex(smoothed, vertex);
    for (let j = -CORNER_WINDOW; j <= CORNER_WINDOW; j++) {
      corners.add((nearest + j + n) % n);
    }
  }

  let outside = total;
  for (const i of corners) outside -= bends[i];
  // What the stretches between corners would turn if the contour were evenly curved
  const evenlyCurved = total * (1 - corners.size / n);
  return outside <= MAX_TURNING_BETWEEN_CORNERS * evenlyCurved;
}

/** Triangle or quadrilateral, regularized into a rectangle when it is one */
function fitFewSided(samples: Pt[], perimeter: number): Candidate | null {
  const vertices = polygonVertices(samples, perimeter, POLYGON_SIMPLIFY);
  if (vertices.length < 3 || vertices.length > 4) return null;

  const error = polygonError(samples, vertices, perimeter);
  if (error > POLYGON_MAX_ERROR) return null;

  if (vertices.length === 3) {
    return { shape: { kind: "polygon", type: "triangle", vertices }, error };
  }

  const rectangle = regularizeRectangle(vertices);
  if (rectangle) {
    return {
      shape: { kind: "polygon", type: "rectangle", vertices: rectangle },
      error,
    };
  }

  return { shape: { kind: "polygon", type: "quadrilateral", vertices }, error };
}

/**
 * Turn a quadrilateral whose corners are all close to 90° into an exact
 * rectangle, or return null if it is not rectangular enough.
 */
function regularizeRectangle(vertices: Pt[]): Pt[] | null {
  for (let i = 0; i < 4; i++) {
    const prev = vertices[(i + 3) % 4];
    const curr = vertices[i];
    const next = vertices[(i + 1) % 4];
    const angle = angleBetween(
      { x: prev.x - curr.x, y: prev.y - curr.y },
      { x: next.x - curr.x, y: next.y - curr.y },
    );
    if (Math.abs(angle - Math.PI / 2) > RIGHT_ANGLE_TOLERANCE) return null;
  }

  // Mean edge orientation modulo 90°, weighted by edge length (angles are
  // multiplied by 4 so that all four edges agree on the same direction)
  let sumCos = 0;
  let sumSin = 0;
  for (let i = 0; i < 4; i++) {
    const a = vertices[i];
    const b = vertices[(i + 1) % 4];
    const len = dist(a, b);
    const angle = Math.atan2(b.y - a.y, b.x - a.x);
    sumCos += len * Math.cos(4 * angle);
    sumSin += len * Math.sin(4 * angle);
  }
  const phi = snapAngle(Math.atan2(sumSin, sumCos) / 4, Math.PI / 2);

  const c = centroid(vertices);
  const u = { x: Math.cos(phi), y: Math.sin(phi) };
  const v = { x: -Math.sin(phi), y: Math.cos(phi) };
  let halfU = 0;
  let halfV = 0;
  for (const p of vertices) {
    halfU += Math.abs((p.x - c.x) * u.x + (p.y - c.y) * u.y);
    halfV += Math.abs((p.x - c.x) * v.x + (p.y - c.y) * v.y);
  }
  halfU /= 4;
  halfV /= 4;

  const corner = (su: number, sv: number): Pt => ({
    x: c.x + u.x * su * halfU + v.x * sv * halfV,
    y: c.y + u.y * su * halfU + v.y * sv * halfV,
  });
  return [corner(-1, -1), corner(1, -1), corner(1, 1), corner(-1, 1)];
}

/**
 * Ramer-Douglas-Peucker on a closed contour: split it at the point farthest
 * from the centroid and at the point farthest from that one (both are sure
 * to be corners), then simplify each half.
 */
function simplifyClosed(points: Pt[], epsilon: number): Pt[] {
  const c = centroid(points);
  const i0 = farthestIndex(points, c);
  const i1 = farthestIndex(points, points[i0]);

  const first = Math.min(i0, i1);
  const second = Math.max(i0, i1);
  const chainA = points.slice(first, second + 1);
  const chainB = [...points.slice(second), ...points.slice(0, first + 1)];

  const simplifiedA = rdp(chainA, epsilon);
  const simplifiedB = rdp(chainB, epsilon);
  return [...simplifiedA.slice(0, -1), ...simplifiedB.slice(0, -1)];
}

function rdp(points: Pt[], epsilon: number): Pt[] {
  if (points.length <= 2) return points;

  const start = points[0];
  const end = points[points.length - 1];
  let maxDist = 0;
  let maxIndex = 0;
  for (let i = 1; i < points.length - 1; i++) {
    const d = distToSegment(points[i], start, end);
    if (d > maxDist) {
      maxDist = d;
      maxIndex = i;
    }
  }

  if (maxDist <= epsilon) return [start, end];

  const left = rdp(points.slice(0, maxIndex + 1), epsilon);
  const right = rdp(points.slice(maxIndex), epsilon);
  return [...left.slice(0, -1), ...right];
}

/**
 * A hand-drawn corner is often rounded, which RDP turns into two close
 * vertices joined by a short bevel. Replace each such pair by the corner the
 * user meant: the intersection of the two neighboring edges.
 */
function mergeRoundedCorners(vertices: Pt[], minEdge: number): Pt[] {
  const result = [...vertices];
  let merged = true;
  while (merged && result.length > 3) {
    merged = false;
    for (let i = 0; i < result.length; i++) {
      const n = result.length;
      const a = result[i];
      const b = result[(i + 1) % n];
      if (dist(a, b) >= minEdge) continue;

      const prev = result[(i + n - 1) % n];
      const next = result[(i + 2) % n];
      const corner = lineIntersection(prev, a, b, next) ?? {
        x: (a.x + b.x) / 2,
        y: (a.y + b.y) / 2,
      };
      // Keep the corner only if it stays near the bevel (nearly parallel
      // edges would send the intersection far away)
      const safeCorner =
        dist(corner, a) <= 2 * minEdge
          ? corner
          : { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };

      result.splice(i, 1, safeCorner);
      result.splice((i + 1) % result.length, 1);
      merged = true;
      break;
    }
  }
  return result;
}

/** Intersection of line (p1, p2) with line (p3, p4), or null if parallel */
function lineIntersection(p1: Pt, p2: Pt, p3: Pt, p4: Pt): Pt | null {
  const d1x = p2.x - p1.x;
  const d1y = p2.y - p1.y;
  const d2x = p4.x - p3.x;
  const d2y = p4.y - p3.y;
  const denom = d1x * d2y - d1y * d2x;
  if (Math.abs(denom) < 1e-9) return null;
  const t = ((p3.x - p1.x) * d2y - (p3.y - p1.y) * d2x) / denom;
  return { x: p1.x + t * d1x, y: p1.y + t * d1y };
}

/** Drop vertices where the contour barely turns, until none is left */
function removeFlatVertices(vertices: Pt[]): Pt[] {
  const result = [...vertices];
  let changed = true;
  while (changed && result.length > 3) {
    changed = false;
    for (let i = 0; i < result.length; i++) {
      const prev = result[(i + result.length - 1) % result.length];
      const curr = result[i];
      const next = result[(i + 1) % result.length];
      const turn =
        Math.PI -
        angleBetween(
          { x: prev.x - curr.x, y: prev.y - curr.y },
          { x: next.x - curr.x, y: next.y - curr.y },
        );
      if (turn < POLYGON_MIN_TURN) {
        result.splice(i, 1);
        changed = true;
        break;
      }
    }
  }
  return result;
}

// ============== Geometry helpers ==============

function dist(a: Pt, b: Pt): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

function pathLength(points: Pt[], closed: boolean): number {
  let length = 0;
  for (let i = 1; i < points.length; i++) {
    length += dist(points[i - 1], points[i]);
  }
  if (closed && points.length > 1) {
    length += dist(points[points.length - 1], points[0]);
  }
  return length;
}

/** Resample a closed contour into `count` points evenly spaced along its length */
function resample(points: Pt[], count: number, closed = true): Pt[] {
  // A closed contour wraps back to its start; an open stroke spreads its
  // samples so that the last one lands on its end point
  const ring = closed ? [...points, points[0]] : points;
  const step = pathLength(points, closed) / (closed ? count : count - 1);
  const result: Pt[] = [{ ...points[0] }];

  let remaining = step;
  for (let i = 1; i < ring.length && result.length < count; i++) {
    let a = ring[i - 1];
    const b = ring[i];
    let segment = dist(a, b);
    while (segment >= remaining && result.length < count) {
      const t = remaining / segment;
      const p = { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
      result.push(p);
      a = p;
      segment -= remaining;
      remaining = step;
    }
    remaining -= segment;
  }

  // Rounding can leave an open stroke one sample short of its end
  if (!closed && result.length < count) {
    result.push({ ...points[points.length - 1] });
  }
  return result;
}

function centroid(points: Pt[]): Pt {
  let x = 0;
  let y = 0;
  for (const p of points) {
    x += p.x;
    y += p.y;
  }
  return { x: x / points.length, y: y / points.length };
}

function boundsOf(points: Pt[]) {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of points) {
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x);
    maxY = Math.max(maxY, p.y);
  }
  return { minX, minY, maxX, maxY };
}

function farthestIndex(points: Pt[], from: Pt): number {
  let best = 0;
  let bestDist = -1;
  for (let i = 0; i < points.length; i++) {
    const d = dist(points[i], from);
    if (d > bestDist) {
      bestDist = d;
      best = i;
    }
  }
  return best;
}

function nearestIndex(points: Pt[], to: Pt): number {
  let best = 0;
  let bestDist = Infinity;
  for (let i = 0; i < points.length; i++) {
    const d = dist(points[i], to);
    if (d < bestDist) {
      bestDist = d;
      best = i;
    }
  }
  return best;
}

/** Unsigned angle between two vectors, in [0, π] */
function angleBetween(a: Pt, b: Pt): number {
  const cross = a.x * b.y - a.y * b.x;
  const dot = a.x * b.x + a.y * b.y;
  return Math.abs(Math.atan2(cross, dot));
}

/** Distance from p to the infinite line through a and b */
function distToLine(p: Pt, a: Pt, b: Pt): number {
  const len = dist(a, b);
  if (len === 0) return dist(p, a);
  return Math.abs((b.x - a.x) * (a.y - p.y) - (a.x - p.x) * (b.y - a.y)) / len;
}

function distToSegment(p: Pt, a: Pt, b: Pt): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return dist(p, a);
  const t = Math.max(
    0,
    Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq),
  );
  return dist(p, { x: a.x + t * dx, y: a.y + t * dy });
}

function distToPolygon(p: Pt, vertices: Pt[]): number {
  let best = Infinity;
  for (let i = 0; i < vertices.length; i++) {
    best = Math.min(
      best,
      distToSegment(p, vertices[i], vertices[(i + 1) % vertices.length]),
    );
  }
  return best;
}

/** Bring an angle into [0, period) */
function normalizeAngle(angle: number, period: number): number {
  return ((angle % period) + period) % period;
}

/** Snap an angle to the nearest multiple of `period` if within AXIS_SNAP */
function snapAngle(angle: number, period: number): number {
  const k = Math.round(angle / period);
  return Math.abs(angle - k * period) <= AXIS_SNAP ? k * period : angle;
}
